import { supabase } from "@/integrations/supabase/client";
import {
  directPathError,
  getStaffSessionToken,
  handleInvalidSession,
  isInvalidSessionError,
} from "@/services/staffSession";

// Staff accounts. update_user_password, create_user_with_password and a direct
// users UPDATE were all callable with the publishable key: any staff account,
// admins included, could be taken over. With a staff token, Admin > Users goes
// through *_by_session wrappers that require an admin session (20260930110000);
// the originals and users UPDATE are revoked from the public key in
// 20260930120000. The direct calls stay only for a browser with no token.
type RpcError = { code?: string; message: string } | null;
const sessionRpc = async <T>(fn: string, args: Record<string, unknown>) =>
  await (supabase.rpc as unknown as (
    name: string,
    params: Record<string, unknown>
  ) => Promise<{ data: T | null; error: RpcError }>)(fn, args);

/** A rejected token or refused public key signs the user out. Returns the error. */
const signOutOnRefusal = <E>(error: E): E => {
  if (isInvalidSessionError(error)) {
    handleInvalidSession();
    return error;
  }
  return directPathError(error);
};

export interface UserManagement {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'operations_manager' | 'clinic_incharge_both' | 'clinic_incharge_forum' | 'clinic_incharge_ngeeann' | 'office_admin' | 'office_user';
  allowed_clinics: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  staff_id?: string;
  staff_name?: string;
}

export interface CreateUserData {
  email: string;
  name: string;
  password: string;
  role: 'admin' | 'operations_manager' | 'clinic_incharge_both' | 'clinic_incharge_forum' | 'clinic_incharge_ngeeann' | 'office_admin' | 'office_user';
  allowed_clinics: string[];
  staff_id?: string;
}

export interface UpdateUserData {
  name?: string;
  role?: 'admin' | 'operations_manager' | 'clinic_incharge_both' | 'clinic_incharge_forum' | 'clinic_incharge_ngeeann' | 'office_admin' | 'office_user';
  allowed_clinics?: string[];
  is_active?: boolean;
  password?: string;
  staff_id?: string | null;
}

export const userService = {
  async getAllUsers(): Promise<UserManagement[]> {
    const { data, error } = await supabase
      .from('user_management')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching users:', error);
      throw error;
    }

    return (data || []) as unknown as UserManagement[];
  },

  async getUserById(id: string): Promise<UserManagement | null> {
    const { data, error } = await supabase
      .from('user_management')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching user:', error);
      return null;
    }

    return data as unknown as UserManagement;
  },

  async createUser(userData: CreateUserData): Promise<{ success: boolean; error?: string }> {
    try {
      // First, we need to use the pgcrypto functions to hash the password
      const sessionToken = getStaffSessionToken();
      const { error } = sessionToken
        ? await sessionRpc<string>('create_user_with_password_by_session', {
            p_session_token: sessionToken,
            p_email: userData.email,
            p_name: userData.name,
            p_password: userData.password,
            p_role: userData.role,
            p_allowed_clinics: userData.allowed_clinics,
          })
        : await supabase.rpc('create_user_with_password', {
            user_email: userData.email,
            user_name: userData.name,
            user_password: userData.password,
            user_role: userData.role,
            user_allowed_clinics: userData.allowed_clinics
          });

      if (error) {
        console.error('Error creating user:', signOutOnRefusal(error));
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Exception creating user:', error);
      return { success: false, error: 'Failed to create user' };
    }
  },

  async updateUser(id: string, updates: UpdateUserData): Promise<{ success: boolean; error?: string }> {
    try {
      const sessionToken = getStaffSessionToken();

      // If password is being updated, use a special function
      if (updates.password) {
        const { error: passwordError } = sessionToken
          ? await sessionRpc<boolean>('update_user_password_by_session', {
              p_session_token: sessionToken,
              p_user_id: id,
              p_new_password: updates.password,
            })
          : await supabase.rpc('update_user_password', {
              user_id: id,
              new_password: updates.password
            });

        if (passwordError) {
          console.error('Error updating password:', signOutOnRefusal(passwordError));
          return { success: false, error: passwordError.message };
        }

        // Remove password from updates
        delete updates.password;
      }

      // Handle staff_id linking
      if ('staff_id' in updates) {
        const staffId = updates.staff_id;
        
        if (staffId) {
          // Link user to staff
          const { data: linkResult, error: linkError } = sessionToken
            ? await sessionRpc<boolean>('link_user_to_staff_by_session', {
                p_session_token: sessionToken,
                p_user_id: id,
                p_staff_id: staffId,
              })
            : await supabase.rpc('link_user_to_staff', {
                p_user_id: id,
                p_staff_id: staffId
              });

          if (linkError) {
            console.error('Error linking user to staff:', signOutOnRefusal(linkError));
            return { success: false, error: 'Failed to link user to staff member' };
          }

          if (!linkResult) {
            return { success: false, error: 'Staff member is already linked to another user' };
          }
        } else {
          // Unlink user from staff
          const { error: unlinkError } = sessionToken
            ? await sessionRpc<null>('unlink_user_from_staff_by_session', {
                p_session_token: sessionToken,
                p_user_id: id,
              })
            : await supabase.rpc('unlink_user_from_staff', {
                p_user_id: id
              });

          if (unlinkError) {
            console.error('Error unlinking user from staff:', signOutOnRefusal(unlinkError));
            return { success: false, error: 'Failed to unlink user from staff' };
          }
        }

        // Remove staff_id from updates as it's handled separately
        delete updates.staff_id;
      }

      // Update other fields if any remain.
      //
      // `.select('id')`, never a bare `.select()`. A bare select asks PostgREST
      // for `*`, which includes password_hash - so this call used to hand a
      // bcrypt hash back to the browser on every user edit, and it was never
      // even read (only `error` is checked below). anon now holds no SELECT
      // privilege on that column, so `*` would fail the whole statement and
      // the edit would stop working entirely. 2026-08-27.
      const { password, staff_id, ...profileUpdates } = updates;
      if (Object.keys(profileUpdates).length > 0) {
        const { error } = sessionToken
          ? await sessionRpc<string>('update_user_by_session', {
              p_session_token: sessionToken,
              p_user_id: id,
              p_payload: profileUpdates,
            })
          : await supabase
              .from('users')
              .update(profileUpdates)
              .eq('id', id)
              .select('id')
              .single();

        if (error) {
          console.error('Error updating user:', signOutOnRefusal(error));
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Exception updating user:', error);
      return { success: false, error: 'Failed to update user' };
    }
  },

  async deactivateUser(id: string): Promise<{ success: boolean; error?: string }> {
    return this.updateUser(id, { is_active: false });
  },

  async activateUser(id: string): Promise<{ success: boolean; error?: string }> {
    return this.updateUser(id, { is_active: true });
  },

  async getAvailableStaff(): Promise<{ id: string; name: string; role: string; status: string }[]> {
    const { data, error } = await supabase.rpc('get_available_staff');
    
    if (error) {
      console.error('Error fetching available staff:', error);
      return [];
    }
    
    return data || [];
  },

  async getAllStaff(): Promise<{ id: string; name: string; role: string; status: string; user_id: string | null }[]> {
    const { data, error } = await supabase
      .from('staff')
      .select('id, name, role, status, user_id')
      .eq('status', 'active')
      .order('name');
    
    if (error) {
      console.error('Error fetching all staff:', error);
      return [];
    }
    
    return data || [];
  }
};